import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarRange, DollarSign, TrendingUp, Bell, Filter, X, Ban,
  MoreVertical, History, Loader2,
} from "lucide-react";
import { formatDateBR, toISODate, STATUS_STYLES } from "@/lib/format";
import { useTerminology } from "@/lib/terminology";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUSES = [
  "Confirmado", "Concluído", "Cancelado pelo barbeiro",
  "Cancelado pelo cliente", "Cliente não compareceu",
];

export default function AgendaTab({ dashboard, barbers, reloadDashboard }) {
  const { label } = useTerminology();
  const [date, setDate] = useState(toISODate(new Date()));
  const [barberId, setBarberId] = useState("");
  const [status, setStatus] = useState("");
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [historyData, setHistoryData] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (date) params.date = date;
      if (barberId) params.barber_id = barberId;
      if (status) params.status = status;
      const { data } = await api.get("/admin/appointments", { params });
      setAppts(data);
    } catch {
      toast.error("Erro ao carregar agenda.");
    } finally {
      setLoading(false);
    }
  }, [date, barberId, status]);

  useEffect(() => { load(); }, [load]);

  const doCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post(`/admin/appointments/${cancelTarget.id}/cancel`, {
        reason: cancelReason || null, by: "barbeiro",
      });
      toast.success(
        data.notification_status === "enviada"
          ? "Agendamento cancelado e cliente notificado."
          : "Agendamento cancelado. Notificação registrada (envio pendente)."
      );
      setCancelTarget(null); setCancelReason("");
      load(); reloadDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar.");
    } finally {
      setCancelling(false);
    }
  };

  const changeStatus = async (a, newStatus) => {
    try {
      await api.put(`/admin/appointments/${a.id}/status`, { status: newStatus });
      toast.success(`Status alterado para ${label(newStatus)}.`);
      load(); reloadDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar status.");
    }
  };

  const openHistory = async (a) => {
    setHistoryFor(a);
    try {
      const { data } = await api.get(`/admin/appointments/${a.id}/history`);
      setHistoryData(data);
    } catch { setHistoryData([]); }
  };

  const isCancelled = (s) => s.startsWith("Cancelado");

  return (
    <div className="fade-in">
      <h1 className="font-display text-2xl sm:text-3xl gold-text mb-1">Painel do Estabelecimento</h1>
      <p className="text-stone-400 font-sans2 text-sm mb-6">
        Resumo de hoje — {dashboard?.today_fmt}
      </p>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <Metric icon={CalendarRange} label="Agendamentos hoje" value={dashboard?.total_today ?? "—"} />
        <Metric icon={DollarSign} label="Faturamento estimado" value={dashboard?.revenue_est ?? "—"} />
        <Metric icon={TrendingUp} label="Profissional mais solicitado" value={dashboard?.top_barber ?? "—"} small />
        <Metric icon={Bell} label="Notificações pendentes" value={dashboard?.pending_notifications ?? 0} />
      </div>

      {/* Filters */}
      <div className="card-surface p-4 mb-5 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2 text-stone-400 text-sm font-sans2">
          <Filter className="w-4 h-4 text-[#D4AF37]" /> Filtros
        </div>
        <div>
          <label className="text-xs text-stone-500 block mb-1 font-sans2">Data</label>
          <input
            type="date"
            data-testid="agenda-filter-date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-[#12141A] border border-[#282C37] rounded-lg px-3 py-2 text-sm text-amber-50 focus:border-[#D4AF37] focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs text-stone-500 block mb-1 font-sans2">Profissional</label>
          <select
            data-testid="agenda-filter-barber"
            value={barberId}
            onChange={(e) => setBarberId(e.target.value)}
            className="bg-[#12141A] border border-[#282C37] rounded-lg px-3 py-2 text-sm text-amber-50 focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="">Todos</option>
            {barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-stone-500 block mb-1 font-sans2">Status</label>
          <select
            data-testid="agenda-filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-[#12141A] border border-[#282C37] rounded-lg px-3 py-2 text-sm text-amber-50 focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="">Todos</option>
            {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </select>
        </div>
        {(barberId || status) && (
          <button
            onClick={() => { setBarberId(""); setStatus(""); }}
            className="text-xs text-stone-400 hover:text-red-400 flex items-center gap-1 font-sans2"
          >
            <X className="w-3.5 h-3.5" /> Limpar
          </button>
        )}
      </div>

      {/* Appointment list */}
      {loading ? (
        <div className="flex justify-center py-10 text-stone-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : appts.length === 0 ? (
        <div className="card-surface p-8 text-center text-stone-400 font-sans2">
          Nenhum agendamento para os filtros selecionados.
        </div>
      ) : (
        <div className="space-y-3">
          {appts.map((a) => (
            <div key={a.id} data-testid={`appointment-row-${a.id}`} className="card-surface p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="font-mono2 text-center sm:w-24 shrink-0">
                <div className="text-lg font-semibold text-[#D4AF37]">{a.start}</div>
                <div className="text-xs text-stone-500">{a.end}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-sans2 font-semibold text-amber-50">{a.client_name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-sans2 ${STATUS_STYLES[a.status]}`}>
                    {label(a.status)}
                  </span>
                </div>
                <div className="text-sm text-stone-400 font-sans2 mt-0.5">
                  {a.service_name} • {a.barber_name} • {a.duration} min
                </div>
                <div className="text-xs text-stone-500 font-mono2 mt-0.5">
                  WhatsApp: +{a.whatsapp}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid={`appointment-history-btn-${a.id}`}
                  onClick={() => openHistory(a)}
                  title="Histórico"
                  className="p-2 rounded-lg text-stone-400 hover:text-[#D4AF37] hover:bg-[#1A1D26] transition-colors"
                >
                  <History className="w-4 h-4" />
                </button>
                {a.status === "Confirmado" && (
                  <>
                    <select
                      data-testid={`appointment-status-select-${a.id}`}
                      onChange={(e) => e.target.value && changeStatus(a, e.target.value)}
                      value=""
                      className="bg-[#12141A] border border-[#282C37] rounded-lg px-2 py-2 text-xs text-amber-50 focus:border-[#D4AF37] focus:outline-none"
                    >
                      <option value="">Alterar status…</option>
                      <option value="Concluído">Concluído</option>
                      <option value="Cliente não compareceu">Não compareceu</option>
                    </select>
                    <button
                      data-testid={`admin-cancel-booking-btn-${a.id}`}
                      onClick={() => setCancelTarget(a)}
                      className="flex items-center gap-1 text-xs bg-red-500/10 text-red-400 border border-red-500/30 px-3 py-2 rounded-lg hover:bg-red-500/20 transition-colors font-sans2"
                    >
                      <Ban className="w-3.5 h-3.5" /> Cancelar
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="bg-[#12141A] border-[#282C37]">
          <DialogHeader>
            <DialogTitle className="font-display text-amber-100">Cancelar agendamento</DialogTitle>
          </DialogHeader>
          {cancelTarget && (
            <div className="text-sm text-stone-400 font-sans2 space-y-1">
              <p>{cancelTarget.client_name} — {cancelTarget.service_name}</p>
              <p>{formatDateBR(cancelTarget.date)} às {cancelTarget.start} com {cancelTarget.barber_name}</p>
            </div>
          )}
          <div>
            <label className="text-sm text-stone-300 font-sans2 mb-1.5 block">Motivo (opcional)</label>
            <textarea
              data-testid="cancel-reason-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Ex.: imprevisto do profissional"
              className="w-full bg-[#0A0B0E] border border-[#282C37] rounded-lg px-3 py-2 text-amber-50 focus:border-[#D4AF37] focus:outline-none font-sans2 text-sm"
            />
            <p className="text-xs text-stone-500 mt-2 font-sans2">
              O histórico será preservado e o cliente receberá uma notificação de cancelamento pelo WhatsApp.
            </p>
          </div>
          <DialogFooter>
            <button onClick={() => setCancelTarget(null)} className="px-4 py-2 text-sm text-stone-400 hover:text-amber-100 font-sans2">
              Voltar
            </button>
            <button
              data-testid="confirm-cancel-btn"
              onClick={doCancel}
              disabled={cancelling}
              className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg font-sans2 font-semibold flex items-center gap-2 disabled:opacity-60"
            >
              {cancelling && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar cancelamento
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="bg-[#12141A] border-[#282C37]">
          <DialogHeader>
            <DialogTitle className="font-display text-amber-100 flex items-center gap-2">
              <History className="w-5 h-5 text-[#D4AF37]" /> Histórico
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {historyData.map((h) => (
              <div key={h.id} className="border-l-2 border-[#D4AF37]/40 pl-3">
                <div className="text-sm text-amber-50 font-sans2 capitalize">{h.action}</div>
                <div className="text-xs text-stone-400 font-sans2">
                  {h.from_status ? `${label(h.from_status)} → ` : ""}{label(h.to_status)} • por {label(h.by)}
                </div>
                {h.reason && <div className="text-xs text-stone-500 italic">Motivo: {h.reason}</div>}
                <div className="text-[10px] text-stone-600 font-mono2">
                  {new Date(h.at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                </div>
              </div>
            ))}
            {historyData.length === 0 && <p className="text-stone-500 text-sm">Sem histórico.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon: Icon, label, value, small }) {
  return (
    <div className="card-surface p-4">
      <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
      <div className={`font-semibold text-amber-50 ${small ? "text-base" : "text-2xl font-mono2"}`}>{value}</div>
      <div className="text-xs text-stone-500 font-sans2 mt-0.5">{label}</div>
    </div>
  );
}
